import React, { useState, useEffect } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Chip,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Grid,
  Alert,
  CircularProgress,
  Pagination,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Divider,
  Avatar,
  useTheme,
  Tooltip,
  Badge
} from '@mui/material';
import {
  Close as CloseIcon,
  Schedule as ScheduleIcon,
  TrendingUp as TrendingUpIcon,
  Today as TodayIcon,
  DateRange as DateRangeIcon,
  Person as PersonIcon,
  AccessTime as AccessTimeIcon,
  CalendarMonth as CalendarIcon,
  WorkHistory as WorkHistoryIcon,
  Refresh as RefreshIcon
} from '@mui/icons-material';
import { makeApiCall } from '../../config/api';
import { formatScheduledDate, todayLocalYmd } from '../../utils/dateUtils';

const EmployeeTimesheetView = ({ employee, open, onClose }) => {
  const theme = useTheme();
  
  // State management
  const [timesheetData, setTimesheetData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [page, setPage] = useState(1);
  const [dateRange, setDateRange] = useState('30'); // Default to last 30 days
  const [customStartDate, setCustomStartDate] = useState('');
  const [customEndDate, setCustomEndDate] = useState('');

  // Load timesheet data
  const loadTimesheetData = async () => {
    if (!employee?.id) return;
    
    try {
      setLoading(true);
      setError('');
      
      let startDate = '';
      let endDate = '';
      
      // Calculate date range based on selection
      // Local calendar days, not UTC (TKT-429): toISOString() rolled the
      // range forward a day for evening users in Ontario.
      if (dateRange === 'custom') {
        startDate = customStartDate;
        endDate = customEndDate;
      } else if (dateRange !== 'all') {
        const daysBack = parseInt(dateRange);
        const start = new Date();
        start.setDate(start.getDate() - daysBack);
        startDate = todayLocalYmd(start);
        endDate = todayLocalYmd();
      }
      
      const params = new URLSearchParams({
        page: page.toString(),
        limit: '20'
      });
      
      if (startDate) params.append('start_date', startDate);
      if (endDate) params.append('end_date', endDate);
      
      const response = await makeApiCall(`api/time-tracking/employee/${employee.id}/timesheet?${params}`);
      
      if (response.ok) {
        const data = await response.json();
        setTimesheetData(data);
      } else {
        throw new Error('Failed to load timesheet data');
      }
      
    } catch (error) {
      console.error('Error loading timesheet:', error);
      setError('Failed to load timesheet data');
    } finally {
      setLoading(false);
    }
  };

  // Load data when dialog opens or dependencies change
  useEffect(() => {
    if (open && employee?.id) {
      loadTimesheetData();
    }
  }, [open, employee?.id, page, dateRange, customStartDate, customEndDate]);

  // Reset page when filters change
  useEffect(() => {
    setPage(1);
  }, [dateRange, customStartDate, customEndDate]);

  // Format time for display
  const formatTime = (dateString) => {
    if (!dateString) return '-';
    const date = new Date(dateString);
    return date.toLocaleTimeString('en-US', {
      hour: 'numeric',
      minute: '2-digit',
      hour12: true
    });
  };

  // Format date for display
  // work_date is a calendar day that arrives as UTC midnight; parsing it with
  // new Date() showed Monday's clock-ins as Sunday in Ontario (TKT-429).
  const formatDate = (dateString) => {
    if (!dateString) return '-';
    return formatScheduledDate(dateString, 'en-US', {
      weekday: 'short',
      month: 'short',
      day: 'numeric',
      year: 'numeric'
    }) || '-';
  };

  // Format hours for display
  const formatHours = (hours) => {
    if (!hours || hours === 0) return '0.0h';
    return `${parseFloat(hours).toFixed(1)}h`;
  };

  // Get status color
  const getStatusColor = (status) => {
    return status === 'active' ? 'warning' : 'success';
  };

  // Get status text
  const getStatusText = (status) => {
    return status === 'active' ? 'In Progress' : 'Complete';
  };

  // Reset dialog state when closing
  const handleClose = () => {
    setTimesheetData(null);
    setPage(1);
    setError('');
    onClose();
  };

  if (!employee) return null;

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth="lg"
      fullWidth
      sx={{
        '& .MuiDialog-paper': {
          height: { xs: '95vh', md: '90vh' },
          maxHeight: { xs: '95vh', md: '90vh' },
          m: { xs: 1, sm: 2 }
        }
      }}
    >
      <DialogTitle>
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            <Avatar sx={{ bgcolor: theme.palette.primary.main }}>
              <PersonIcon />
            </Avatar>
            <Box>
              <Typography variant="h6" component="div">
                {employee.employee_name} - Timesheet
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Employee ID: {employee.id}
                {!employee.user_id && ' • No System Access'}
              </Typography>
            </Box>
          </Box>
          <IconButton onClick={handleClose} size="large">
            <CloseIcon />
          </IconButton>
        </Box>
      </DialogTitle>

      <DialogContent sx={{ p: { xs: 2, md: 3 } }}>
        {/* Filters */}
        <Card sx={{ mb: 3 }}>
          <CardContent sx={{ p: { xs: 2, md: 2 }, '&:last-child': { pb: { xs: 2, md: 2 } } }}>
            <Grid container spacing={2} alignItems="center">
              <Grid size={{ xs: 12, sm: 6, md: 4 }}>
                <FormControl fullWidth size="small">
                  <InputLabel>Date Range</InputLabel>
                  <Select
                    value={dateRange}
                    label="Date Range"
                    onChange={(e) => setDateRange(e.target.value)}
                  >
                    <MenuItem value="7">Last 7 days</MenuItem>
                    <MenuItem value="30">Last 30 days</MenuItem>
                    <MenuItem value="90">Last 3 months</MenuItem>
                    <MenuItem value="365">Last year</MenuItem>
                    <MenuItem value="all">All time</MenuItem>
                    <MenuItem value="custom">Custom range</MenuItem>
                  </Select>
                </FormControl>
              </Grid>
              
              {dateRange === 'custom' && (
                <>
                  <Grid size={{ xs: 12, sm: 3, md: 2 }}>
                    <TextField
                      fullWidth
                      label="Start Date"
                      type="date"
                      size="small"
                      value={customStartDate}
                      onChange={(e) => setCustomStartDate(e.target.value)}
                      InputLabelProps={{ shrink: true }}
                    />
                  </Grid>
                  <Grid size={{ xs: 12, sm: 3, md: 2 }}>
                    <TextField
                      fullWidth
                      label="End Date"
                      type="date"
                      size="small"
                      value={customEndDate}
                      onChange={(e) => setCustomEndDate(e.target.value)}
                      InputLabelProps={{ shrink: true }}
                    />
                  </Grid>
                </>
              )}
              
              <Grid size="auto">
                <Tooltip title="Refresh">
                  <IconButton 
                    onClick={loadTimesheetData} 
                    color="primary" 
                    disabled={loading}
                  >
                    <RefreshIcon />
                  </IconButton>
                </Tooltip>
              </Grid>
            </Grid>
          </CardContent>
        </Card>

        {/* Loading State */}
        {loading && (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
            <CircularProgress />
          </Box>
        )}

        {/* Error State */}
        {error && (
          <Alert severity="error" sx={{ mb: 3 }} onClose={() => setError('')}>
            {error}
          </Alert>
        )}

        {/* No System Access */}
        {!employee.user_id && !loading && (
          <Alert severity="info" sx={{ mb: 3 }}>
            This employee does not have system access and cannot clock in/out. 
            Link them to a user account to enable time tracking.
          </Alert>
        )}

        {/* Timesheet Data */}
        {timesheetData && !loading && (
          <>
            {/* Summary Stats */}
            <Grid container spacing={2} sx={{ mb: 3 }}>
              <Grid size={{ xs: 6, sm: 3 }}>
                <Card>
                  <CardContent sx={{ textAlign: 'center', p: 2, '&:last-child': { pb: 2 } }}>
                    <AccessTimeIcon color="primary" sx={{ fontSize: 32, mb: 1 }} />
                    <Typography variant="h6" color="primary">
                      {formatHours(timesheetData.summary.total_hours)}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      Total Hours
                    </Typography>
                  </CardContent>
                </Card>
              </Grid>
              
              <Grid size={{ xs: 6, sm: 3 }}>
                <Card>
                  <CardContent sx={{ textAlign: 'center', p: 2, '&:last-child': { pb: 2 } }}>
                    <CalendarIcon color="info" sx={{ fontSize: 32, mb: 1 }} />
                    <Typography variant="h6" color="info.main">
                      {timesheetData.summary.days_worked}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      Days Worked
                    </Typography>
                  </CardContent>
                </Card>
              </Grid>
              
              <Grid size={{ xs: 6, sm: 3 }}>
                <Card>
                  <CardContent sx={{ textAlign: 'center', p: 2, '&:last-child': { pb: 2 } }}>
                    <TrendingUpIcon color="success" sx={{ fontSize: 32, mb: 1 }} />
                    <Typography variant="h6" color="success.main">
                      {formatHours(timesheetData.summary.avg_hours_per_day)}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      Avg Hours/Day
                    </Typography>
                  </CardContent>
                </Card>
              </Grid>
              
              <Grid size={{ xs: 6, sm: 3 }}>
                <Card>
                  <CardContent sx={{ textAlign: 'center', p: 2, '&:last-child': { pb: 2 } }}>
                    <WorkHistoryIcon color="warning" sx={{ fontSize: 32, mb: 1 }} />
                    <Typography variant="h6" color="warning.main">
                      {timesheetData.summary.total_sessions}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      Sessions
                    </Typography>
                  </CardContent>
                </Card>
              </Grid>
            </Grid>

            {/* Timesheet Entries */}
            <Card>
              <CardContent sx={{ p: 0 }}>
                {timesheetData.entries && timesheetData.entries.length === 0 ? (
                  <Box sx={{ textAlign: 'center', py: 8 }}>
                    <AccessTimeIcon sx={{ fontSize: 64, color: 'text.secondary', mb: 2, opacity: 0.5 }} />
                    <Typography variant="h6" color="text.secondary" gutterBottom>
                      No Time Records Yet
                    </Typography>
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
                      {employee.employee_name} hasn't logged any time entries for the selected period.
                    </Typography>
                    {employee.user_id ? (
                      <Typography variant="body2" color="text.secondary">
                        Time entries will appear here once {employee.employee_name} starts using the time tracking system.
                      </Typography>
                    ) : (
                      <Alert severity="info" sx={{ mt: 2, mx: 4 }}>
                        This employee needs system access to track time. Link them to a user account to enable time tracking.
                      </Alert>
                    )}
                  </Box>
                ) : (
                  <>
                    {/* Desktop Table */}
                    <Box sx={{ display: { xs: 'none', md: 'block' } }}>
                      <TableContainer>
                        <Table>
                          <TableHead>
                            <TableRow>
                              <TableCell>Date</TableCell>
                              <TableCell>Clock In</TableCell>
                              <TableCell>Clock Out</TableCell>
                              <TableCell>Hours</TableCell>
                              <TableCell>Status</TableCell>
                              <TableCell>Notes</TableCell>
                            </TableRow>
                          </TableHead>
                          <TableBody>
                            {timesheetData.entries.map((entry) => (
                              <TableRow key={entry.id} hover>
                                <TableCell>
                                  <Typography variant="body2" sx={{ fontWeight: 'medium' }}>
                                    {formatDate(entry.work_date)}
                                  </Typography>
                                </TableCell>
                                <TableCell>
                                  <Typography variant="body2">
                                    {formatTime(entry.clock_in_time)}
                                  </Typography>
                                </TableCell>
                                <TableCell>
                                  <Typography variant="body2">
                                    {formatTime(entry.clock_out_time)}
                                  </Typography>
                                </TableCell>
                                <TableCell>
                                  <Typography 
                                    variant="body2" 
                                    sx={{ fontWeight: 'medium', color: 'primary.main' }}
                                  >
                                    {formatHours(entry.hours_worked)}
                                  </Typography>
                                </TableCell>
                                <TableCell>
                                  <Chip
                                    label={getStatusText(entry.session_status)}
                                    color={getStatusColor(entry.session_status)}
                                    size="small"
                                  />
                                </TableCell>
                                <TableCell>
                                  <Typography 
                                    variant="body2" 
                                    color="text.secondary"
                                    sx={{ 
                                      maxWidth: 200,
                                      overflow: 'hidden',
                                      textOverflow: 'ellipsis',
                                      whiteSpace: 'nowrap'
                                    }}
                                  >
                                    {entry.notes || '-'}
                                  </Typography>
                                </TableCell>
                              </TableRow>
                            ))}
                          </TableBody>
                        </Table>
                      </TableContainer>
                    </Box>

                    {/* Mobile Card View */}
                    <Box sx={{ display: { xs: 'block', md: 'none' }, p: 2 }}>
                      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                        {timesheetData.entries.map((entry) => (
                          <Card key={entry.id} variant="outlined">
                            <CardContent sx={{ p: 2, '&:last-child': { pb: 2 } }}>
                              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 2 }}>
                                <Box>
                                  <Typography variant="subtitle2" sx={{ fontWeight: 'bold' }}>
                                    {formatDate(entry.work_date)}
                                  </Typography>
                                  <Typography variant="body2" color="text.secondary">
                                    {formatTime(entry.clock_in_time)} - {formatTime(entry.clock_out_time)}
                                  </Typography>
                                </Box>
                                <Box sx={{ textAlign: 'right' }}>
                                  <Typography 
                                    variant="h6" 
                                    color="primary" 
                                    sx={{ fontWeight: 'bold', mb: 0.5 }}
                                  >
                                    {formatHours(entry.hours_worked)}
                                  </Typography>
                                  <Chip
                                    label={getStatusText(entry.session_status)}
                                    color={getStatusColor(entry.session_status)}
                                    size="small"
                                  />
                                </Box>
                              </Box>
                              {entry.notes && (
                                <Typography variant="body2" color="text.secondary">
                                  <strong>Notes:</strong> {entry.notes}
                                </Typography>
                              )}
                            </CardContent>
                          </Card>
                        ))}
                      </Box>
                    </Box>
                  </>
                )}
              </CardContent>
            </Card>

            {/* Pagination */}
            {timesheetData.pagination.totalPages > 1 && (
              <Box sx={{ display: 'flex', justifyContent: 'center', mt: 3 }}>
                <Pagination
                  count={timesheetData.pagination.totalPages}
                  page={page}
                  onChange={(event, value) => setPage(value)}
                  color="primary"
                  size="large"
                />
              </Box>
            )}
          </>
        )}
      </DialogContent>

      <DialogActions sx={{ p: 3, pt: 0 }}>
        <Button onClick={handleClose} variant="outlined" size="large">
          Close
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default EmployeeTimesheetView;
